const validAggregate = (column) => column.aggregate === 'sum' || column.aggregate === 'mean';
export const isNumericColumn = (column) => Boolean(column.numeric || validAggregate(column));

function finiteNumber(value) {
  if (value === null || value === undefined || (typeof value === 'string' && !value.trim())) return null;
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseReportNumber(value) {
  return finiteNumber(value) ?? 0;
}

export function clampColumnWidth(value, fallback = 160) {
  const width = Number(value);
  return Math.max(80, Math.min(600, Math.round(Number.isFinite(width) ? width : fallback)));
}

export function columnWidth(column, view = {}) {
  const fallback = column.width ?? (isNumericColumn(column) ? 120 : 180);
  return clampColumnWidth(view.columnWidths?.[column.key] ?? fallback, fallback);
}

export function leadingLabelSpan(columns) {
  const firstNumeric = columns.findIndex(isNumericColumn);
  return Math.max(1, firstNumeric < 0 ? columns.length : firstNumeric);
}

export function filterReportRows(rows, columns, filters = {}) {
  const active = columns.filter((column) => String(filters[column.key] ?? '').trim());
  if (!active.length) return [...rows];
  return rows.filter((row) => active.every((column) => {
    const value = String(row[column.key] ?? '').toLocaleLowerCase();
    const filter = String(filters[column.key] ?? '').trim().toLocaleLowerCase();
    return value.includes(filter) || (isNumericColumn(column) && value.replace(/,/g, '').includes(filter.replace(/,/g, '')));
  }));
}

export function sortReportRows(rows, columns, sort = {}) {
  const column = columns.find((candidate) => candidate.key === sort.key);
  if (!column) return [...rows];
  const direction = sort.direction === 'desc' ? -1 : 1;
  return rows.map((row, index) => ({ row, index })).sort((left, right) => {
    const comparison = isNumericColumn(column)
      ? parseReportNumber(left.row[column.key]) - parseReportNumber(right.row[column.key])
      : String(left.row[column.key] ?? '').localeCompare(String(right.row[column.key] ?? ''), 'en', { numeric: true, sensitivity: 'base' });
    return direction * comparison || left.index - right.index;
  }).map((entry) => entry.row);
}

// Only explicitly requested aggregates are calculated. Means ignore missing or
// invalid values, but include genuine zero values.
export function aggregateReportRows(rows, columns) {
  const values = Object.create(null);
  for (const column of columns.filter(validAggregate)) {
    let total = 0, count = 0;
    for (const row of rows) {
      const value = finiteNumber(row[column.key]);
      if (value !== null) { total += value; count += 1; }
    }
    values[column.key] = column.aggregate === 'mean' ? (count ? total / count : 0) : total;
  }
  return { count: rows.length, values };
}

export function buildReportGroups(rows, columns, fields = [], sort = {}, parent = []) {
  const knownColumns = new Map(columns.map((column) => [column.key, column]));
  const allowedFields = [...new Set(fields)].filter((field) => knownColumns.has(field) && knownColumns.get(field).allowGroup !== false);
  if (!allowedFields.length) return [];
  const field = allowedFields[0], column = knownColumns.get(field), buckets = new Map();
  for (const row of rows) {
    const raw = row[field];
    const value = isNumericColumn(column)
      ? (finiteNumber(raw) === null ? '' : String(finiteNumber(raw)))
      : String(raw ?? '').trim();
    if (!buckets.has(value)) buckets.set(value, []);
    buckets.get(value).push(row);
  }
  const sortColumn = knownColumns.get(sort.key);
  return [...buckets].map(([value, items]) => {
    const path = [...parent, [field, value]];
    return {
      key: JSON.stringify(path), field, value, label: value || 'Unassigned', depth: parent.length,
      rows: items, totals: aggregateReportRows(items, columns),
      children: buildReportGroups(items, columns, allowedFields.slice(1), sort, path),
    };
  }).sort((left, right) => {
    const byValue = isNumericColumn(column)
      ? (left.value === '' ? -1 : right.value === '' ? 1 : parseReportNumber(left.value) - parseReportNumber(right.value))
      : left.label.localeCompare(right.label, 'en', { numeric: true, sensitivity: 'base' });
    const byAggregate = sortColumn && validAggregate(sortColumn) && sort.key !== field;
    const comparison = byAggregate ? left.totals.values[sort.key] - right.totals.values[sort.key] : byValue;
    const descending = sort.direction === 'desc' && (sort.key === field || byAggregate);
    return (descending ? -comparison : comparison) || byValue;
  });
}

export function reorderGroup(fields, field, index) {
  const next = fields.filter((candidate) => candidate !== field);
  next.splice(Math.max(0, Math.min(next.length, index)), 0, field);
  return next;
}

export function normalizeGridView(input, columns, defaultView = {}) {
  const known = new Set(columns.map((column) => column.key));
  const groupable = new Set(columns.filter((column) => column.allowGroup !== false).map((column) => column.key));
  const candidate = input?.version === 2 ? input : defaultView;
  const rawFields = Array.isArray(candidate.groupBy) ? candidate.groupBy : Array.isArray(defaultView.groupBy) ? defaultView.groupBy : [];
  const groupBy = [...new Set(rawFields.filter((field) => groupable.has(field)))];
  const expanded = [...new Set((Array.isArray(candidate.expanded) ? candidate.expanded : []).filter((key) => {
    if (typeof key !== 'string' || key.length > 4096) return false;
    try {
      const pairs = JSON.parse(key);
      return Array.isArray(pairs) && pairs.length > 0 && pairs.length <= groupBy.length && pairs.every((pair, index) => Array.isArray(pair) && pair.length === 2 && pair[0] === groupBy[index] && typeof pair[1] === 'string');
    } catch { return false; }
  }))].slice(-512);
  const columnWidths = Object.create(null);
  for (const field of known) {
    const width = candidate.columnWidths?.[field];
    if (Number.isInteger(width) && width >= 80 && width <= 600) columnWidths[field] = width;
  }
  const view = { version: 2, grouped: Boolean(groupBy.length), groupBy, expanded, columnWidths };
  const requestedHidden = new Set(Array.isArray(candidate.hiddenColumns) ? candidate.hiddenColumns : []);
  const hiddenColumns = [...known].filter((field) => typeof field === 'string' && requestedHidden.has(field));
  // Keep the first configured column visible if a stale or external preference
  // hides every column. Hiding a column does not discard its other settings.
  if (known.size > 0 && hiddenColumns.length === known.size) hiddenColumns.shift();
  if (hiddenColumns.length) view.hiddenColumns = hiddenColumns;
  while (new TextEncoder().encode(JSON.stringify(view)).length > 128 * 1024 && view.expanded.length) view.expanded.shift();
  return view;
}
